import { shuffle } from "../Helpers.tsx";

/**
 * One page's choices, dealt evenly in two directions at once (ADR 0122 §Dealing,
 * generalised for ADR 0126).
 *
 * A choice is a pairing of a row and a column (an operation and a kind; a sign
 * and a type of slope). A fair shuffle of the crossed list gives every pairing
 * `floor` or `ceil` of its share, but it can hand the extras to two pairings that
 * share a row. Here the crossed list is laid out as a Latin square - round `r`
 * pairs row `i` with column `(i + r) mod columns` - so any prefix of it is
 * balanced in both directions, and the extras are a prefix. The rows and columns
 * are shuffled first so *which* get the extra is still chosen by the seed rather
 * than fixed.
 *
 * Draws once for the rows, once for the columns, then once for the sequence, in
 * that order: ADR 0122's sheets depend on it.
 */
export function latinDealer<R, C>(rows: R[], columns: C[]): (count: number) => { row: R; column: C }[] {
    return (count) => {
        if (count <= 0) {
            return [];
        }
        const shuffledRows = shuffle(rows);
        const shuffledColumns = shuffle(columns);
        const latin: { row: R; column: C }[] = [];
        for (let round = 0; round < shuffledColumns.length; round++) {
            shuffledRows.forEach((row, i) => {
                latin.push({ row, column: shuffledColumns[(i + round) % shuffledColumns.length] });
            });
        }
        const base = Math.floor(count / latin.length);
        const remainder = count - base * latin.length;
        const sequence: { row: R; column: C }[] = [];
        for (let i = 0; i < base; i++) {
            sequence.push(...latin);
        }
        sequence.push(...latin.slice(0, remainder));
        return shuffle(sequence);
    };
}
